import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import SectionLoader from "components/ui/loaders/SectionLoader";
import SelectDropDown from "components/ui/forms/SelectDropDown";
import { useAuth } from "provider/AuthContext";
import Auth from "service/AuthService";
import { API_URL } from "helper/Constants";
import { AsyncPaginate } from "react-select-async-paginate";

export const roleQueryParams = {
  Partner: "partnerId",
  SuperAgent: "superAgentId",
  Agent: "agentId",
  User: "playerId",
};

const debouncePromise = (fn, delay = 300) => {
  let timer;
  let resolvers = [];
  return (...args) =>
    new Promise((resolve) => {
      if (timer) clearTimeout(timer);
      resolvers.push(resolve);

      timer = setTimeout(async () => {
        try {
          const result = await fn(...args);
          resolvers.forEach((r) => r(result));
        } finally {
          resolvers = [];
        }
      }, delay);
    });
};

const RoleBasedDropdowns = ({
  userLoading,
  showSelectRole,
  baseRole,
  setBaseRole,
  selectedRole,
  setSelectedRole,
  selectedInputValues,
  setSelectedInputValues,
}) => {
  const { user } = useAuth();
  const authServ = useMemo(() => new Auth(API_URL), []);

  const [inputValues, setInputValues] = useState({});
  const [suggestionMenuStates, setSuggestionMenuStates] = useState({});

  const hierarchy = ["Owner", "Partner", "SuperAgent", "Agent", "User"];
  const userRoleIndex = hierarchy.indexOf(user?.role);
  const filteredHierarchy = hierarchy.filter(
    (role) => hierarchy.indexOf(role) > userRoleIndex
  );

  const handleChange = (option, currentRole) => {
    const normalizedRole = hierarchy.find(
      (item) => item.toLowerCase() === option.value.toLowerCase()
    );
    const currentIndex = hierarchy.indexOf(normalizedRole);
    setBaseRole(normalizedRole);
    setSelectedRole((prevValues) => {
      const updatedValues = { ...prevValues, [currentRole]: option.value };
      hierarchy.slice(currentIndex + 1).forEach((lowerLevel) => {
        updatedValues[lowerLevel] = null;
      });
      return updatedValues;
    });
  };

  const baseRoleIndex = hierarchy.indexOf(baseRole);
  let rolesToRender = [];
  if (showSelectRole) {
    rolesToRender =
      baseRole !== null
        ? filteredHierarchy.slice(0, filteredHierarchy.indexOf(baseRole) + 1)
        : [];
  } else {
    rolesToRender =
      baseRoleIndex !== -1 ? hierarchy.slice(baseRoleIndex + 1) : [];
  }

  const handleInputChange = (value, role) => {
    setInputValues((prev) => ({
      ...prev,
      [role]: value,
    }));
    if (value.length >= 3) {
      setSuggestionMenuStates((prev) => ({
        ...prev,
        [role]: true,
      }));
    } else {
      setSuggestionMenuStates((prev) => ({
        ...prev,
        [role]: false,
      }));
    }
  };

  const handleMenuClose = (role) => {
    setSuggestionMenuStates((prev) => ({
      ...prev,
      [role]: false,
    }));
  };

  const fetchAllUsers = useCallback(
    async (inputValue, role) => {
      if (inputValue.length < 3) return [];

      try {
        let response;
        if (user?.role === "Owner") {
          response = await authServ.getAllUsers({
            searchText: inputValue,
            role: role
          });
        } else {
          response = await authServ.getUsersByCreatorId(user._id, {
            searchText: inputValue,
            role: role
          });
        }

        const options = response.users?.map((item) => ({
          value: item._id,
          label: item.username,
        })) || [];

        return options;
      } catch (error) {
        console.error("Error fetching users:", error);
        return [];
      }
    },
    [user, authServ]
  );

  const debouncedFetchAllUsers = useMemo(
    () => debouncePromise(fetchAllUsers, 1000),
    [fetchAllUsers]
  );

  if (userLoading) {
    return (
      <div className="text-center w-full">
        <SectionLoader bgColor="light" />
      </div>
    );
  }

  async function loadOptions(_search, loadedOptions) {
    const start = loadedOptions.length;
    const end = start + 10;
    const response = await authServ.getAllUsers({
      searchText: _search,
      role: "User",
      start,
      end
    });
    const options = response.users.map(item => ({ value: item._id, label: item.username }));

    return {
      options,
      hasMore: options.length === (end - start)
    };
  }


  // Factory function to create a loadOptions function for a specific role
  const createLoadOptions = (role) => {
    return async function loadOptions(_search, loadedOptions) {
      const start = loadedOptions.length;
      const end = start + 10;
 
      setSelectedRole(role);
      const response = await authServ.getAllUsers({
        searchText: _search,
        role: role,  // dynamically set role here
        start,
        end,
      });

      const options = response.users.map(item => ({
        value: item._id,
        label: item.username,
      }));

      return {
        options,
        hasMore: options.length === (end - start),
      };
    };
  };


  return (
    <>
      <div className="w-full lg:flex gap-3">
        {showSelectRole && (
          <div className="relative mb-4 w-full lg:min-w-[400px] lg:max-w-[400px]">
            <SelectDropDown
              label="Role"
              placeholder="Select option"
              selectedOption={selectedRole ? selectedRole["role"]?.value : []}
              options={filteredHierarchy.map((role) => ({
                value: role,
                label: role === "SuperAgent" ? "Super Agent" : role,
              }))}
              handleChange={(option) => handleChange(option, "role")}
            />
          </div>
        )}
        {/* {rolesToRender.map((role) => (
          <div key={role} className="relative mb-4 w-full lg:max-w-80">
            <SelectDropDown
              label={role}
              placeholder={"Type 3+ letters"}
              selectedOption={selectedRole ? selectedRole[role]?.value : []}

              loadOptions={(inputValue, callback) => {
                debouncedFetchAllUsers(inputValue, role).then(callback);
              }}
              handleChange={(option) =>
                setSelectedInputValues((prev) => ({
                  ...prev,
                  [role]: option.value,
                }))
              }
              inputValue={inputValues[role] || ""}
              onInputChange={(value) => handleInputChange(value, role)}
              menuIsOpen={suggestionMenuStates[role] || false}
              onMenuClose={() => handleMenuClose(role)}
              isSearchable={true}
            />
          </div>
        ))} */}

        {rolesToRender.map((role) => (
          <div key={role} className="relative mb-4 w-full lg:max-w-80">
            <h3>{role}</h3>
            {/* <AsyncPaginate loadOptions={loadOptions} /> */}
            <AsyncPaginate
            value={selectedInputValues[role] || null} //using input value as value.
  loadOptions={createLoadOptions(role)}
  onChange={(option) =>
    setSelectedInputValues((prev) => ({
      ...prev,
      [role]: option, // ✅ store it under the correct role
    }))
  }
/>
          </div>
        ))}
      </div>
    </>
  );
};

export default RoleBasedDropdowns;
